//React
import React, { ReactNode, useMemo } from 'react';

//Manitne
import {
  Button,
  Card,
  Divider,
  Flex,
  Stack,
  Text,
  useComputedColorScheme,
} from '@mantine/core';

//Components
import {
  AllKeys,
  ClaimDetailSection,
  ClaimProviderSection,
  useActiveForm,
  ClaimTariffSection,
  ClaimAdditionalInfoSection,
  ClaimCommentSection,
  claimCommentSectionKeys,
  SKClaimDetailSectionKeys,
} from 'pages/claims/components';

import { useIsSKClaim } from 'hooks/useIsSKClaim';

// Utils
import { buttonTextColor } from 'utils/buttonUtils';

//Interfaces
import { Provider } from 'interfaces';
import { useTranslation } from '@refinedev/core';

interface SharedDataSectionProps {
  index: number;
  onCancel: () => void;
  onNext: () => void;
  editComponentsMap: Record<string, ReactNode>;
  providers: Provider[];
  providerSectionKeys: AllKeys[];
  detailSectionKeys: AllKeys[];
  additionalInfoKeys: AllKeys[];
  tariffSectionKeys: AllKeys[];
  totalClaims: number;
}

export const SharedDataSection: React.FC<SharedDataSectionProps> = ({
  index,
  onCancel,
  onNext,
  editComponentsMap,
  providers,
  providerSectionKeys,
  detailSectionKeys,
  additionalInfoKeys,
  tariffSectionKeys,
}) => {
  const { form, prefix } = useActiveForm();
  const { translate } = useTranslation();
  const isSKClaim = useIsSKClaim(form, prefix);
  const computedColorScheme = useComputedColorScheme('dark');
  const claimDetailSectionKeys = useMemo(
    () => [
      ...detailSectionKeys,
      ...(isSKClaim ? SKClaimDetailSectionKeys : []),
    ],
    [detailSectionKeys, isSKClaim]
  );

  return (
    <Stack gap="md">
      <Card shadow="xs" p="lg" radius="md" withBorder>
        <ClaimProviderSection
          index={index.toString()}
          providers={providers}
          title="Provider"
          keys={providerSectionKeys}
          cols={4}
          editComponentsMap={editComponentsMap}
          type="bulk"
        />
        <Divider variant="dotted" my="md" />
        <Text fw={500} mb="sm">
          Shared Claim Data
        </Text>
        <ClaimDetailSection
          index={index.toString()}
          title="Claim Details"
          keys={claimDetailSectionKeys}
          cols={3}
          editComponentsMap={editComponentsMap}
          type="bulk"
        />
        <Divider variant="dotted" my="md" />

        <ClaimTariffSection
          index={index.toString()}
          title="Tariffs"
          keys={tariffSectionKeys}
          cols={1}
          editComponentsMap={editComponentsMap}
          type="bulk"
        />
        <Divider variant="dotted" my="md" />
        <ClaimAdditionalInfoSection
          index={index.toString()}
          title="Additional Info"
          keys={additionalInfoKeys}
          cols={4}
          editComponentsMap={editComponentsMap}
          type="bulk"
        />
        <Divider variant="dotted" my="md" />
        <ClaimCommentSection
          index={index.toString()}
          title={translate('pages:claims.comments.title', 'Comments')}
          keys={claimCommentSectionKeys}
          cols={1}
          editComponentsMap={editComponentsMap}
          type="bulk"
        />
      </Card>
      <Flex justify={'center'} gap={'lg'} style={{ marginTop: '1%' }}>
        <Button
          variant="outline"
          radius="md"
          color="gray"
          c={buttonTextColor(computedColorScheme, false)}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="outline"
          c={buttonTextColor(computedColorScheme, false)}
          onClick={onNext}
        >
          Next
        </Button>
      </Flex>
    </Stack>
  );
};
